/**
 * StatusBar — 底部状态栏
 *
 * 三区域布局：左（文档上下文）│ 中（执行 & 服务状态）│ 右（系统 & 面板）
 * 设计规范：docs/03-Implementation/02-Editor-Core/system/statusbar-design.md
 */
import type { ParsedDocument } from "@magic-md/core";
import type { DocumentActivityEvent } from "@magic-md/engine-client/protocol";
import { REF_ID_RE, REF_RE } from "@magic-md/core/patterns";
import { useT } from "@magic-md/i18n/react";
import {
    AlertTriangle,
    CheckCircle2,
    Info,
    Loader2,
    Power,
    PowerOff,
    XCircle,
} from "lucide-react";
import {
    memo,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import { cn } from "../lib/utils";
import { ExtensionStatusBarItems } from "../extensions/ExtensionContributions";
import { usePluginRegistryVersion } from "../hooks/usePluginUI";
import { getPlatformService } from "../platform";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { useEditorStore } from "../store/editor-store";
import { useEngineOverviewStore } from "../store/engine-overview-store";
import { useLogStore } from "../store/log-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { ProductIcon } from "../theme/runtime/theme-icons";

/* ── MCP tool name → i18n key map ── */
const TOOL_LABEL_KEYS: Record<string, string> = {
    // Read-only
    get_document_content: "statusbar.tools.getDocumentContent",
    read_output: "statusbar.tools.readOutput",
    list_definitions: "statusbar.tools.listDefinitions",
    list_plugins: "statusbar.tools.listPlugins",
    get_plugin_schema: "statusbar.tools.getPluginSchema",
    get_plugin_runtime_info: "statusbar.tools.getPluginRuntimeInfo",
    list_files: "statusbar.tools.listFiles",
    list_documents: "statusbar.tools.listDocuments",
    validate_document: "statusbar.tools.validateDocument",
    open_document: "statusbar.tools.openDocument",
    close_document: "statusbar.tools.closeDocument",
    // Execution
    start_execution: "statusbar.tools.startExecution",
    get_execution_state: "statusbar.tools.getExecutionState",
    cancel_execution: "statusbar.tools.cancelExecution",
    // Definition CRUD
    create_definition: "statusbar.tools.createDefinition",
    edit_definition: "statusbar.tools.editDefinition",
    delete_definition: "statusbar.tools.deleteDefinition",
    rename_definition: "statusbar.tools.renameDefinition",
    // For-block CRUD
    create_for_block: "statusbar.tools.createForBlock",
    edit_for_block: "statusbar.tools.editForBlock",
    delete_for_block: "statusbar.tools.deleteForBlock",
    // Content-block CRUD
    create_content_block: "statusbar.tools.createContentBlock",
    edit_content_block: "statusbar.tools.editContentBlock",
    delete_content_block: "statusbar.tools.deleteContentBlock",
    // Import CRUD
    create_import: "statusbar.tools.createImport",
    edit_import: "statusbar.tools.editImport",
    delete_import: "statusbar.tools.deleteImport",
    // Use-block CRUD
    create_use_block: "statusbar.tools.createUseBlock",
    edit_use_block: "statusbar.tools.editUseBlock",
    delete_use_block: "statusbar.tools.deleteUseBlock",
    // Meta CRUD
    create_meta: "statusbar.tools.createMeta",
    edit_meta: "statusbar.tools.editMeta",
    // Text / render
    insert_text: "statusbar.tools.insertText",
    replace_text: "statusbar.tools.replaceText",
    add_render: "statusbar.tools.addRender",
    delete_render: "statusbar.tools.deleteRender",
};

const EMPTY_DOCUMENT_ACTIVITIES: DocumentActivityEvent[] = [];

type EditorStoreState = ReturnType<typeof useEditorStore.getState>;

function selectFocusedTabId(state: EditorStoreState): string {
    return (
        state.panes?.find((pane) => pane.paneId === state.focusedPaneId)?.tabId ??
        state.activeTabId
    );
}

type ExecutionStatsSummary = {
    running: number;
    success: number;
    error: number;
    idle: number;
    totalExecuted: number;
    lastDuration: number | null;
    runningIds: string[];
    runningDetails: Array<{
        id: string;
        label: string;
        percent: number | null;
    }>;
    errorDetails: string[];
};

const EMPTY_EXECUTION_STATS: ExecutionStatsSummary = {
    running: 0,
    success: 0,
    error: 0,
    idle: 0,
    totalExecuted: 0,
    lastDuration: null,
    runningIds: [],
    runningDetails: [],
    errorDetails: [],
};

function selectExecutionStatsKey(
    state: ReturnType<typeof usePluginExecutionStore.getState>,
): string {
    let running = 0;
    let success = 0;
    let error = 0;
    let idle = 0;
    let lastDuration: number | null = null;
    let lastStartedAt: number | null = null;
    const runningIds: string[] = [];
    const runningDetails: ExecutionStatsSummary["runningDetails"] = [];
    const errorDetails: string[] = [];

    const describeError = (
        unitId: string,
        entry: {
            result?: { message?: string } | null;
            output?: unknown;
            actionName?: string | null;
        },
    ) => {
        const outputError =
            entry.output &&
                typeof entry.output === "object" &&
                "__error" in entry.output
                ? (entry.output as Record<string, unknown>).__error
                : null;
        const outputMessage =
            typeof outputError === "object" && outputError !== null
                ? (outputError as { message?: unknown }).message
                : outputError;
        const message =
            entry.result?.message ??
            (typeof outputMessage === "string" ? outputMessage : undefined);
        errorDetails.push(message ? `${unitId}: ${message}` : unitId);
    };

    const aggregateEntry = (
        unitId: string,
        entry: {
            status: string;
            startedAt: number | null;
            duration: number | null;
            result?: { message?: string } | null;
            output?: unknown;
            actionName?: string | null;
            progress?: {
                message?: string;
                percent?: number;
            };
        },
    ) => {
        switch (entry.status) {
            case "running":
                running++;
                runningIds.push(unitId);
                runningDetails.push({
                    id: unitId,
                    label:
                        entry.progress?.message?.trim() ||
                        entry.actionName?.trim() ||
                        unitId,
                    percent:
                        typeof entry.progress?.percent === "number"
                            ? entry.progress.percent
                            : null,
                });
                break;
            case "success":
                success++;
                break;
            case "error":
                error++;
                describeError(unitId, entry);
                break;
            default:
                idle++;
                break;
        }
        if (
            entry.duration != null &&
            entry.startedAt != null &&
            (entry.status === "success" || entry.status === "error") &&
            (lastDuration === null || entry.startedAt > (lastStartedAt ?? 0))
        ) {
            lastDuration = entry.duration;
            lastStartedAt = entry.startedAt;
        }
    };

    for (const [, entry] of state.executions) {
        aggregateEntry(entry.unitId, entry);
    }
    for (const [, entry] of state.forExecutions) {
        aggregateEntry(entry.forId, entry);
    }
    for (const [, entry] of state.contentExecutions) {
        aggregateEntry(entry.contentId, entry);
    }
    for (const [, entry] of state.importExecutions) {
        aggregateEntry(entry.importId, entry);
    }
    for (const [, entry] of state.useExecutions) {
        aggregateEntry(entry.useId, entry);
    }

    return JSON.stringify({
        running,
        success,
        error,
        idle,
        totalExecuted: success + error,
        lastDuration,
        runningIds,
        runningDetails,
        errorDetails,
    } satisfies ExecutionStatsSummary);
}

function parseExecutionStatsKey(key: string): ExecutionStatsSummary {
    if (!key) return EMPTY_EXECUTION_STATS;
    return JSON.parse(key) as ExecutionStatsSummary;
}

function selectRefIssuesKey(
    parsedDocument: ParsedDocument | null,
    state: ReturnType<typeof usePluginExecutionStore.getState>,
): string {
    if (!parsedDocument) return "0:0";
    let notFound = 0;
    let notExecuted = 0;
    for (const [, def] of parsedDocument.definitions) {
        const configStr = JSON.stringify(def.config);
        const refs = configStr.match(new RegExp(REF_RE.source, "g"));
        if (!refs) continue;

        for (const ref of refs) {
            const refId = ref.match(REF_ID_RE)?.[1];
            if (!refId) continue;

            const refInner = ref.replace("$ref{", "").replace("}", "");
            const dotIdx = refInner.indexOf(".");
            const accessor = dotIdx >= 0 ? refInner.slice(dotIdx + 1) : "";
            if (accessor === "body" || accessor.startsWith("body.")) {
                if (!parsedDocument.contentDefinitions?.has(refId)) {
                    notFound++;
                }
                continue;
            }

            if (!parsedDocument.definitions.has(refId)) {
                notFound++;
            } else {
                const target = parsedDocument.definitions.get(refId)!;
                const execOutput = state.executions.get(refId)?.output;
                if (!execOutput && !target.output) {
                    notExecuted++;
                }
            }
        }
    }
    return `${notFound}:${notExecuted}`;
}

function ExecutionStatusSegment({
    parsedDocument,
    definitions,
}: {
    parsedDocument: ParsedDocument | null;
    definitions: number;
}) {
    const t = useT("editor");
    const execStatsKey = usePluginExecutionStore(selectExecutionStatsKey);
    const execStats = useMemo(
        () => parseExecutionStatsKey(execStatsKey),
        [execStatsKey],
    );
    const refIssuesKey = usePluginExecutionStore((s) =>
        selectRefIssuesKey(parsedDocument, s),
    );
    const [notFoundRaw = "0", notExecutedRaw = "0"] = refIssuesKey.split(":");
    const refIssues = {
        notFound: Number(notFoundRaw),
        notExecuted: Number(notExecutedRaw),
        total: Number(notFoundRaw) + Number(notExecutedRaw),
    };
    const resetLogFilter = useLogStore((s) => s.resetFilter);
    const setLogActiveTab = useLogStore((s) => s.setActiveTab);
    const showExecutionErrors = useCallback(() => {
        resetLogFilter();
        setLogActiveTab("logs");
    }, [resetLogFilter, setLogActiveTab]);
    const firstRunning = execStats.runningDetails[0] ?? null;
    const firstRunningLabel =
        firstRunning && firstRunning.label !== firstRunning.id
            ? `${firstRunning.id} · ${firstRunning.label}`
            : firstRunning?.label;
    const firstRunningPercent =
        typeof firstRunning?.percent === "number"
            ? `${Math.round(firstRunning.percent)}%`
            : null;

    if (definitions <= 0) return null;

    return (
        <div className="flex min-w-0 shrink-0 items-center gap-1.5 overflow-hidden whitespace-nowrap">
            {execStats.running > 0 && (
                <span
                    className="flex min-w-0 items-center gap-0.5 theme-accent-text"
                    style={getThemeAccentStyle("running")}
                    title={execStats.runningIds.join(", ")}
                >
                    <Loader2
                        size={11}
                        className="animate-spin"
                    />
                    <span className="max-w-[200px] truncate">
                        {execStats.runningIds.length === 1
                            ? [firstRunningLabel, firstRunningPercent]
                                .filter(Boolean)
                                .join(" · ")
                            : `${execStats.runningIds[0]} +${execStats.runningIds.length - 1}`}
                    </span>
                </span>
            )}
            {execStats.success > 0 && (
                <span className="flex items-center gap-0.5">
                    <CheckCircle2 size={11} />
                    {execStats.success}
                </span>
            )}
            {execStats.error > 0 && (
                <button
                    type="button"
                    className="flex items-center gap-0.5 theme-accent-text hover:underline"
                    style={getThemeAccentStyle("error")}
                    onClick={showExecutionErrors}
                    title={[
                        t("statusbar.execution.viewLogs"),
                        ...execStats.errorDetails.slice(0, 6),
                        execStats.errorDetails.length > 6
                            ? t("statusbar.execution.moreErrors", {
                                count: execStats.errorDetails.length - 6,
                            })
                            : "",
                    ]
                        .filter(Boolean)
                        .join("\n")}
                    aria-label={t("statusbar.execution.errorAria", {
                        count: execStats.error,
                    })}
                >
                    <XCircle size={11} />
                    {execStats.error}
                </button>
            )}
            {refIssues.total > 0 && (
                <span
                    className="flex items-center gap-0.5 theme-accent-text"
                    style={getThemeAccentStyle("warning")}
                    title={[
                        refIssues.notFound > 0
                            ? t("statusbar.ref.notFound", {
                                count: refIssues.notFound,
                            })
                            : "",
                        refIssues.notExecuted > 0
                            ? t("statusbar.ref.notExecuted", {
                                count: refIssues.notExecuted,
                            })
                            : "",
                    ]
                        .filter(Boolean)
                        .join("  ")}
                >
                    <AlertTriangle size={11} />
                    {refIssues.total} ref
                </span>
            )}
            {execStats.lastDuration != null &&
                execStats.totalExecuted > 0 && (
                    <span className="text-muted-foreground/70">
                        {execStats.lastDuration}ms
                    </span>
                )}
        </div>
    );
}

function countDocumentStats(content: string): { lines: number; words: number } {
    let lines = 1;
    let words = 0;
    let inWord = false;

    for (let i = 0; i < content.length; i++) {
        const code = content.charCodeAt(i);
        if (code === 10) lines++;

        const isWhitespace =
            code <= 32 ||
            code === 0xa0 ||
            code === 0x1680 ||
            (code >= 0x2000 && code <= 0x200a) ||
            code === 0x2028 ||
            code === 0x2029 ||
            code === 0x202f ||
            code === 0x205f ||
            code === 0x3000;
        if (isWhitespace) {
            inWord = false;
        } else if (!inWord) {
            words++;
            inWord = true;
        }
    }

    return { lines, words };
}

const FocusedDocumentStats = memo(function FocusedDocumentStats() {
    const t = useT("editor");
    const stats = useEditorStore(
        useShallow((s) => {
            const focusedPane = s.panes?.find(
                (pane) => pane.paneId === s.focusedPaneId,
            );
            const focusedTabId = focusedPane?.tabId ?? s.activeTabId;
            const tab = s.tabs.find((item) => item.id === focusedTabId);
            return {
                cursorLine: focusedPane?.cursorLine ?? s.cursorLine,
                cursorCol: focusedPane?.cursorCol ?? s.cursorCol,
                content: tab?.content ?? "",
            };
        }),
    );
    const docStats = useMemo(
        () => countDocumentStats(stats.content),
        [stats.content],
    );

    return (
        <div className="flex min-w-0 shrink-0 items-center gap-2 whitespace-nowrap">
            <span className="shrink-0">
                {t("statusbar.cursor", {
                    line: stats.cursorLine,
                    col: stats.cursorCol,
                })}
            </span>
            <span className="hidden text-border text-[11px] sm:inline">
                │
            </span>
            <span className="hidden shrink-0 sm:inline">
                {t("statusbar.documentStats", {
                    lines: docStats.lines,
                    words: docStats.words,
                })}
            </span>
        </div>
    );
});

interface StatusDetails {
    definitions: number;
    pluginLoading: ReturnType<typeof pluginUIRegistry.loadingSnapshot>;
    registeredPlugins: number;
    sessionCount: number;
    sidecarCount: number;
}

function StatusDetailsPopover({
    buttonRef,
    details,
    onClose,
    onOpenEngineDetail,
    onOpenSessionDetail,
    open,
}: {
    buttonRef: RefObject<HTMLButtonElement | null>;
    details: StatusDetails;
    onClose: () => void;
    onOpenEngineDetail: () => void;
    onOpenSessionDetail: () => void;
    open: boolean;
}) {
    const t = useT("editor");
    const panelRef = useRef<HTMLDivElement | null>(null);
    const firstPluginName = details.pluginLoading.names[0];
    const remainingPlugins = Math.max(0, details.pluginLoading.names.length - 1);
    const pluginStatusLabel = details.pluginLoading.isLoading
        ? firstPluginName
            ? remainingPlugins > 0
                ? t("statusbar.plugins.loadingMore", {
                    name: firstPluginName,
                    count: remainingPlugins,
                })
                : t("statusbar.plugins.loadingNamed", { name: firstPluginName })
            : t("statusbar.plugins.loading")
        : t("statusbar.plugins", { count: details.registeredPlugins });
    const pluginStatusTitle = details.pluginLoading.isLoading
        ? [
            pluginStatusLabel,
            details.pluginLoading.names.length > 0
                ? details.pluginLoading.names.join(", ")
                : "",
        ].filter(Boolean).join("\n")
        : pluginStatusLabel;

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (panelRef.current?.contains(target)) return;
            if (buttonRef.current?.contains(target)) return;
            onClose();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("pointerdown", handlePointerDown, true);
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("pointerdown", handlePointerDown, true);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [buttonRef, onClose, open]);

    if (!open || typeof document === "undefined") return null;

    const openSessionDetail = () => {
        onOpenSessionDetail();
        onClose();
    };
    const openEngineDetail = () => {
        onOpenEngineDetail();
        onClose();
    };

    return createPortal(
        <div
            ref={panelRef}
            data-testid="statusbar-details-popover"
            role="dialog"
            aria-label={t("statusbar.details.title")}
            className="fixed bottom-8 right-3 z-50 w-[min(300px,calc(100vw-24px))] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
        >
            <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-[11px] font-medium">
                <Info size={13} />
                <span>{t("statusbar.details.title")}</span>
            </div>
            <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 px-3 py-2 text-[11px]">
                <dt className="text-muted-foreground">
                    {t("statusbar.details.definitions")}
                </dt>
                <dd>{t("statusbar.definitions", { count: details.definitions })}</dd>

                <dt className="text-muted-foreground">
                    {t("statusbar.details.plugins")}
                </dt>
                <dd
                    className="min-w-0 max-w-[170px] justify-self-start truncate text-left"
                    title={pluginStatusTitle}
                >
                    {pluginStatusLabel}
                </dd>

                <dt className="text-muted-foreground">
                    {t("statusbar.details.sessions")}
                </dt>
                <dd className="justify-self-start">
                    <button
                        type="button"
                        className="rounded text-left hover:bg-foreground/10 hover:text-foreground"
                        onClick={openSessionDetail}
                    >
                        {t("statusbar.sessions.label", {
                            count: details.sessionCount,
                        })}
                    </button>
                </dd>

                <dt className="text-muted-foreground">
                    {t("statusbar.details.sidecars")}
                </dt>
                <dd className="justify-self-start">
                    <button
                        type="button"
                        className="rounded text-left hover:bg-foreground/10 hover:text-foreground"
                        onClick={openEngineDetail}
                    >
                        {t("statusbar.sidecars.label", {
                            count: details.sidecarCount,
                        })}
                    </button>
                </dd>

                <dt className="text-muted-foreground">
                    {t("statusbar.details.format")}
                </dt>
                <dd>MD</dd>

                <dt className="text-muted-foreground">
                    {t("statusbar.details.encoding")}
                </dt>
                <dd>UTF-8</dd>
            </dl>
        </div>,
        document.body,
    );
}

/* ── Main component ── */

export const StatusBar = memo(function StatusBar() {
    const t = useT("editor");
    const focusedTabId = useEditorStore(selectFocusedTabId);
    const parsedDocument = useEditorStore(
        (s) =>
            s.parsedDocumentMap.get(focusedTabId) ??
            (focusedTabId === s.activeTabId ? s.parsedDocument : null),
    );
    const statusMessage = useEditorStore((s) => s.statusMessage);
    const toggleLogPanel = useLogStore((s) => s.togglePanel);
    const logIsOpen = useLogStore((s) => s.isOpen);
    const unreadErrors = useLogStore((s) => s.unreadErrors);
    const logEntryCount = useLogStore((s) => s.entries.length);
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const mcpEndpoint = useEditorStore((s) => s.mcpEndpoint);
    const mcpConnectedClients = useEditorStore((s) => s.mcpConnectedClients);
    const setMcpStatus = useEditorStore((s) => s.setMcpStatus);
    const setShowMcpDetail = useEditorStore((s) => s.setShowMcpDetail);
    const setShowSessionDetail = useEditorStore((s) => s.setShowSessionDetail);
    const setShowEngineDetail = useEditorStore((s) => s.setShowEngineDetail);

    /* ── MCP start / stop toggle ── */
    const [mcpLoading, setMcpLoading] = useState(false);
    const mcpIsRunning = mcpStatus === "running";
    const mcpIsBusy = mcpStatus === "starting" || mcpStatus === "stopping";

    const handleMcpToggle = useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation(); // don't trigger the detail-view click
        const engine = getPlatformService().engine;
        setMcpLoading(true);
        try {
            if (mcpIsRunning) {
                if (engine.mcpStop) {
                    await engine.mcpStop();
                    setMcpStatus("stopped");
                }
            } else {
                if (engine.mcpStart) {
                    setMcpStatus("starting");
                    const startResult = await engine.mcpStart();
                    const full = await engine.mcpStatus?.();
                    if (full) {
                        setMcpStatus(
                            full.status,
                            full.endpoint,
                            full.port,
                            full.connectedClients,
                            full.clients,
                            full.tools,
                            full.prompts,
                            full.resources,
                            full.knownClients,
                        );
                    } else {
                        setMcpStatus("running", startResult.endpoint, startResult.port);
                    }
                }
            }
        } catch {
            setMcpStatus("error");
        } finally {
            setMcpLoading(false);
        }
    }, [mcpIsRunning, setMcpStatus]);

    // Per-document activity: subscribe only to the focused tab's session.
    const focusedSessionId = usePluginExecutionStore((s) =>
        s.tabSessionMap.get(focusedTabId) ?? null,
    );
    const currentDocActivities = useEditorStore((s) =>
        focusedSessionId
            ? (s.documentActivities.get(focusedSessionId) ?? EMPTY_DOCUMENT_ACTIVITIES)
            : EMPTY_DOCUMENT_ACTIVITIES,
    );
    // Active = not yet faded (write/execute/read operations still open, or recent completed)
    const activeActors = useMemo(() => {
        const actors: { name: string; operation: string }[] = [];
        const seen = new Set<string>();
        const opLabel = (op: string) => {
            switch (op) {
                case "read": return t("statusbar.activity.read");
                case "write": return t("statusbar.activity.write");
                case "execute": return t("statusbar.activity.execute");
                case "completed": return t("statusbar.activity.completed");
                case "failed": return t("statusbar.activity.failed");
                default: return op;
            }
        };
        for (const activity of currentDocActivities) {
            const key = activity.source.type === "mcp"
                ? `mcp:${activity.source.sessionId}`
                : activity.source.type;
            if (!seen.has(key)) {
                seen.add(key);
                const name = activity.source.type === "mcp"
                    ? (activity.source.clientName ?? "MCP Client")
                    : activity.source.type === "gateway"
                        ? "Gateway"
                        : activity.source.type === "external-edit"
                            ? t("statusbar.source.externalEdit")
                            : t("statusbar.source.unknown");
                const label =
                    (activity.toolName && TOOL_LABEL_KEYS[activity.toolName]
                        ? t(TOOL_LABEL_KEYS[activity.toolName])
                        : undefined)
                    ?? opLabel(activity.operation);
                actors.push({ name, operation: label });
            }
        }
        return actors;
    }, [currentDocActivities, t]);
    const sessionCount = useEngineOverviewStore((s) => s.sessions.length);
    const sidecarCount = useEngineOverviewStore(
        (s) => s.sidecars.filter((sidecar) => sidecar.alive).length,
    );
    const retainOverviewScopes = useEngineOverviewStore((s) => s.retainScopes);

    useEffect(() => {
        return retainOverviewScopes(["sessions", "sidecars"]);
    }, [retainOverviewScopes]);

    const definitions =
        (parsedDocument?.definitions.size ?? 0) +
        (parsedDocument?.forDefinitions?.size ?? 0) +
        (parsedDocument?.contentDefinitions?.size ?? 0) +
        (parsedDocument?.imports?.length ?? 0) +
        (parsedDocument?.uses?.length ?? 0);

    // Count registered plugins
    const pluginRegistryVersion = usePluginRegistryVersion();
    const pluginRegistryStatus = useMemo(() => ({
        registeredPlugins: pluginUIRegistry.names().length,
        loading: pluginUIRegistry.loadingSnapshot(),
    }), [pluginRegistryVersion]);
    const { registeredPlugins } = pluginRegistryStatus;

    /* ── MCP status text & color ── */
    const mcpAccent =
        mcpStatus === "error"
            ? "error"
            : mcpStatus === "starting" || mcpStatus === "stopping"
                ? "warning"
                : mcpStatus === "running"
                    ? "success"
                    : null;
    const mcpSuffix =
        mcpStatus === "running"
            ? ""
            : mcpStatus === "starting"
                ? t("statusbar.mcp.suffix.starting")
                : mcpStatus === "stopping"
                    ? t("statusbar.mcp.suffix.stopping")
                    : mcpStatus === "error"
                        ? t("statusbar.mcp.suffix.error")
                        : "";
    const statusDetailsButtonRef = useRef<HTMLButtonElement | null>(null);
    const [statusDetailsOpen, setStatusDetailsOpen] = useState(false);
    const closeStatusDetails = useCallback(() => {
        setStatusDetailsOpen(false);
    }, []);
    const openSessionDetailFromPopover = useCallback(() => {
        setShowSessionDetail(true);
    }, [setShowSessionDetail]);
    const openEngineDetailFromPopover = useCallback(() => {
        setShowEngineDetail(true);
    }, [setShowEngineDetail]);
    const statusDetails = useMemo<StatusDetails>(
        () => ({
            definitions,
            pluginLoading: pluginRegistryStatus.loading,
            registeredPlugins,
            sessionCount,
            sidecarCount,
        }),
        [
            definitions,
            pluginRegistryStatus.loading,
            registeredPlugins,
            sessionCount,
            sidecarCount,
        ],
    );

    return (
        <footer
            data-testid="statusbar"
            className="relative flex h-6 min-h-6 min-w-0 shrink-0 flex-nowrap items-center overflow-hidden whitespace-nowrap border-t border-border bg-muted/30 px-2 text-[11px] text-muted-foreground select-none sm:px-3"
        >
            {/* ── 左区域：光标 + 文档统计 + 执行状态 ── */}
            <FocusedDocumentStats />
            <div className="hidden min-w-0 shrink-0 items-center gap-2 overflow-hidden md:flex">
                <ExtensionStatusBarItems align="left" />
            </div>

            {/* 中区域：执行状态 + status message */}
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden px-2 sm:gap-3 sm:px-3">
                {/* Status message (transient feedback) */}
                {statusMessage && (
                    <span className="min-w-0 truncate text-foreground/80 animate-in fade-in duration-200">
                        {statusMessage}
                    </span>
                )}

                <ExecutionStatusSegment
                    parsedDocument={parsedDocument}
                    definitions={definitions}
                />
                <div className="hidden min-w-0 items-center gap-2 overflow-hidden lg:flex">
                    <ExtensionStatusBarItems align="center" />
                </div>
            </div>

            {/* ── 右区域：服务 + 面板 + 诊断入口 ── */}
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                {/* MCP — always visible with start/stop toggle */}
                <div
                    className="flex max-w-[150px] shrink-0 cursor-pointer items-center gap-1 overflow-hidden transition-colors hover:text-foreground sm:max-w-[220px]"
                    onClick={() => setShowMcpDetail(true)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setShowMcpDetail(true);
                        }
                    }}
                    title={mcpEndpoint ?? t("statusbar.mcp.detailTitle")}
                    role="button"
                    tabIndex={0}
                >
                    <ProductIcon slot="status.mcp" size={11} />
                    <span
                        className={cn("shrink-0", mcpAccent && "theme-accent-text")}
                        style={mcpAccent ? getThemeAccentStyle(mcpAccent) : undefined}
                    >
                        MCP{mcpStatus !== "stopped" ? mcpSuffix : ""}
                    </span>
                    {mcpStatus === "running" && activeActors.length > 0 && (
                        <span className="hidden min-w-0 truncate text-muted-foreground sm:inline">
                            · {activeActors[0].name}
                            {" "}
                            <span className="opacity-75">{activeActors[0].operation}</span>
                            {activeActors.length > 1 ? ` +${activeActors.length - 1}` : ""}
                        </span>
                    )}
                    {mcpStatus === "running" &&
                        activeActors.length === 0 &&
                        mcpConnectedClients > 0 && (
                            <span className="hidden text-muted-foreground sm:inline">
                                · {mcpConnectedClients}
                            </span>
                        )}
                    {/* Start / Stop micro-button */}
                    <button
                        type="button"
                        disabled={mcpIsBusy || mcpLoading}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMcpToggle(e);
                        }}
                        className={cn(
                            "inline-flex items-center justify-center rounded px-1 py-0.5 transition-colors",
                            "hover:bg-foreground/10",
                            (mcpIsBusy || mcpLoading) && "opacity-40 cursor-not-allowed",
                        )}
                        title={
                            mcpIsRunning
                                ? t("statusbar.mcp.stop")
                                : t("statusbar.mcp.start")
                        }
                    >
                        {mcpIsBusy || mcpLoading ? (
                            <Loader2 size={10} className="animate-spin" />
                        ) : mcpIsRunning ? (
                            <PowerOff size={10} />
                        ) : (
                            <Power size={10} />
                        )}
                    </button>
                </div>

                {/* Extension-contributed status-bar items */}
                <div className="hidden max-w-[220px] min-w-0 items-center gap-2 overflow-hidden xl:flex">
                    <ExtensionStatusBarItems align="right" />
                </div>

                {/* Log panel toggle */}
                <button
                    type="button"
                    className={cn(
                        "flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors",
                        logIsOpen
                            ? "text-primary bg-primary/10"
                            : unreadErrors > 0
                                ? "theme-accent-chip animate-pulse"
                                : "text-muted-foreground hover:text-foreground",
                    )}
                    style={
                        !logIsOpen && unreadErrors > 0
                            ? getThemeAccentStyle("error")
                            : undefined
                    }
                    onClick={toggleLogPanel}
                    aria-label={
                        logIsOpen
                            ? t("statusbar.logs.hide")
                            : t("statusbar.logs.show")
                    }
                    title={
                        logIsOpen
                            ? t("statusbar.logs.hide")
                            : t("statusbar.logs.show")
                    }
                >
                    <ProductIcon slot="status.logs" size={11} />
                    {unreadErrors > 0 && (
                        <span className="text-[9px] font-bold">
                            {unreadErrors}
                        </span>
                    )}
                    {logIsOpen && logEntryCount > 0 && (
                        <span className="text-[9px]">{logEntryCount}</span>
                    )}
                </button>

                <button
                    ref={statusDetailsButtonRef}
                    type="button"
                    className={cn(
                        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors",
                        statusDetailsOpen
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-foreground/10 hover:text-foreground",
                    )}
                    aria-label={t("statusbar.details.open")}
                    aria-expanded={statusDetailsOpen}
                    aria-haspopup="dialog"
                    title={t("statusbar.details.open")}
                    onClick={() => setStatusDetailsOpen((open) => !open)}
                >
                    <Info size={12} />
                </button>
            </div>
            <StatusDetailsPopover
                buttonRef={statusDetailsButtonRef}
                details={statusDetails}
                onClose={closeStatusDetails}
                onOpenEngineDetail={openEngineDetailFromPopover}
                onOpenSessionDetail={openSessionDetailFromPopover}
                open={statusDetailsOpen}
            />
        </footer>
    );
});
